/**
 * Scaled.info Theme License Validator - Production Version
 * Secure validation without exposing sensitive data
 */

(function() {
  'use strict';

  class ScaledLicenseValidator {
    constructor(config = {}) {
      this.apiUrl = config.apiUrl || 'https://app.scaled.info/api/v1/theme-keys/validate';
      this.shopDomain = window.Shopify ? window.Shopify.shop : null;
      this.cacheKey = 'scaled_lv_cache';
      this.cacheDuration = 3600000; // 1 hour
      this.isValidated = false;
      this.validationResult = null;
    }

    async validate(licenseKey) {
      if (!licenseKey || licenseKey.trim() === '') {
        return this.errorResponse('no_license', 'License key required');
      }

      if (!this.shopDomain) {
        return this.errorResponse('invalid_domain', 'Shop domain not available');
      }

      // Check cache first
      const cached = this.getCachedValidation(licenseKey);
      if (cached !== null) {
        this.isValidated = true;
        this.validationResult = cached;
        return cached;
      }

      // Call validation API
      try {
        const response = await fetch(this.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            licenseKey: licenseKey.trim().toUpperCase(),
            shopDomain: this.shopDomain
          })
        });

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          return this.errorResponse('invalid_response', 'Server error');
        }

        const data = await response.json();

        if (response.ok && data.valid) {
          this.setCachedValidation(licenseKey, data);
          this.isValidated = true;
          this.validationResult = data;
          return data;
        } else {
          this.clearCache();
          this.isValidated = false;
          this.validationResult = data;
          return data;
        }
      } catch (error) {
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          return this.errorResponse('network_error', 'Connection error');
        }
        return this.errorResponse('network_error', 'Validation failed');
      }
    }

    getCachedValidation(licenseKey) {
      try {
        const cached = localStorage.getItem(this.cacheKey);
        if (!cached) return null;

        const { key, result, timestamp } = JSON.parse(cached);

        // Validate cache
        if (key !== this.hashKey(licenseKey.trim().toUpperCase())) {
          this.clearCache();
          return null;
        }

        if (Date.now() - timestamp < this.cacheDuration) {
          return result;
        }

        this.clearCache();
        return null;
      } catch {
        return null;
      }
    }

    setCachedValidation(licenseKey, result) {
      try {
        localStorage.setItem(this.cacheKey, JSON.stringify({
          key: this.hashKey(licenseKey.trim().toUpperCase()),
          result: result,
          timestamp: Date.now()
        }));
      } catch {}
    }

    clearCache() {
      try {
        localStorage.removeItem(this.cacheKey);
        this.isValidated = false;
        this.validationResult = null;
      } catch {}
    }

    // Simple hash to avoid storing actual key
    hashKey(key) {
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        const char = key.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return hash.toString(36);
    }

    errorResponse(status, message) {
      return {
        valid: false,
        status: status,
        message: message
      };
    }

    isValid() {
      return this.isValidated && this.validationResult && this.validationResult.valid;
    }
  }

  // Register validator
  window.ScaledLicenseValidator = ScaledLicenseValidator;

  // Dispatch ready event
  if (typeof Event === 'function') {
    window.dispatchEvent(new Event('scaled:validator-loaded'));
  } else {
    var event = document.createEvent('Event');
    event.initEvent('scaled:validator-loaded', true, true);
    window.dispatchEvent(event);
  }
})();